import React, { useState } from "react";
import { Pencil, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm text-gray-600 mb-1.5">{label} {required && <span className="text-rose-500">*</span>}</label>
    {children}
  </div>
);
const inputCls = "w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition";

const Profile = () => {
  const { user, refresh } = useAuth();
  const [editing, setEditing] = useState(false);

  // MFA state
  const [setup, setSetup] = useState(null); // {secret, qr}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const startSetup = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { setMsg(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post("/auth/mfa/verify", { code });
      setSetup(null); setCode(""); setMsg("MFA enabled successfully");
      await refresh();
    } catch (e) { setMsg(e?.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setMsg("");
    try { await api.post("/auth/mfa/disable"); await refresh(); setMsg("MFA disabled"); }
    catch { } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl card-soft border border-gray-100 p-6 md:p-8 fade-up">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your account details, organization, and security settings.</p>
        </div>
        <button onClick={() => setEditing((e) => !e)} className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 border border-indigo-300 hover:bg-indigo-50 px-3.5 py-1.5 rounded-lg transition">
          <Pencil size={14} /> Edit
        </button>
      </div>

      <div className="flex items-center gap-4 mt-6">
        <div className="w-14 h-14 rounded-full purple-gradient text-white flex items-center justify-center text-lg font-semibold">{initials}</div>
        <div>
          <p className="font-semibold text-gray-900">{user?.name}</p>
          <p className="text-sm text-gray-500">{user?.email}{user?.trn ? ` · ${user.trn}` : ""}</p>
          <span className="inline-block mt-1 text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{user?.role}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-8">
        <h2 className="text-xl font-bold text-gray-900">{user?.company}</h2>
        <button onClick={() => setEditing((e) => !e)} className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:underline"><Pencil size={13} /> Edit</button>
      </div>

      {editing && (
        <div className="mt-4 fade-up grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Name" required><input defaultValue={user?.name} className={inputCls} /></Field>
          <Field label="Email" required><input defaultValue={user?.email} className={inputCls} /></Field>
          <Field label="Country" required><input defaultValue={user?.country} className={inputCls} /></Field>
          <Field label="Language" required><select className={inputCls}><option>English</option><option>Arabic</option></select></Field>
        </div>
      )}

      <div className="border-t border-gray-100 my-6" />

      {/* MFA */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500"><ShieldCheck size={20} /></div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Multi-Factor Authentication (MFA)</h3>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${user?.mfa_enabled ? "text-emerald-600 bg-emerald-50" : "text-orange-600 bg-orange-50"}`}>{user?.mfa_enabled ? "ENABLED" : "DISABLED"}</span>
          </div>
          <p className="text-sm text-gray-500">Secure your account with an authenticator app (TOTP).</p>
        </div>
        <div className="ml-auto">
          {user?.mfa_enabled ? (
            <button onClick={disable} disabled={busy} className="text-sm font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 px-4 py-2 rounded-lg transition">Disable MFA</button>
          ) : !setup ? (
            <button onClick={startSetup} disabled={busy} className="inline-flex items-center gap-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg transition">{busy && <Loader2 size={14} className="animate-spin" />} Enable MFA</button>
          ) : null}
        </div>
      </div>

      {msg && <div className="mt-4 text-sm inline-flex items-center gap-2 text-emerald-600"><CheckCircle2 size={15} /> {msg}</div>}

      {setup && !user?.mfa_enabled && (
        <div className="mt-5 rounded-xl border border-gray-100 p-5 fade-up flex flex-col md:flex-row gap-6 items-start">
          <img src={setup.qr} alt="MFA QR" className="w-40 h-40 rounded-lg border border-gray-100" />
          <div className="flex-1">
            <p className="text-sm text-gray-700 font-medium">1. Scan this QR code with Google Authenticator, Authy, or 1Password.</p>
            <p className="text-xs text-gray-400 mt-1">Or enter this secret manually:</p>
            <code className="inline-block mt-1 text-xs bg-gray-100 px-2 py-1 rounded font-mono break-all">{setup.secret}</code>
            <p className="text-sm text-gray-700 font-medium mt-4">2. Enter the 6-digit code to confirm:</p>
            <div className="flex items-center gap-2 mt-2">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="000000" className="w-32 text-center tracking-widest px-3 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-indigo-200" />
              <button onClick={verify} disabled={busy || code.length !== 6} className="inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 px-4 py-2 rounded-lg transition">{busy && <Loader2 size={14} className="animate-spin" />} Verify &amp; enable</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
